// Client helpers for workspace files: types mirroring the API, upload with progress, open/download.
import { api, guestCred } from './api';

export interface Version {
  id: string;
  version: number;
  size: number;
  content_type: string;
  uploaded_at: string;
  uploaded_by_kind: 'admin' | 'member' | 'guest';
  uploaded_by_name: string;
}

export interface FileEntry {
  id: string;
  name: string;
  inline: boolean;
  current: Version | null;
  versions: Version[];
}

export const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.docx,.xlsx,.pptx,.doc,.xls,.ppt,.zip,.dwg,.mp4,.dxf,.ifc,.csv,.txt';
export const MAX_BYTES = 95 * 1024 * 1024;

const ALLOWED = new Set(ACCEPT.split(',').map((e) => e.slice(1)));
/** True when the server's allowlist takes this name's extension. */
export const allowedName = (name: string) => ALLOWED.has(/\.([A-Za-z0-9]+)$/.exec(name)?.[1]?.toLowerCase() ?? '');

export interface PickedFile {
  file: File;
  /** Path relative to the drop or the picked folder ("Lampiran/A12.pdf"), or just the name. */
  path: string;
}

/** Files from a folder picker (webkitdirectory) or a plain multi-file picker, with their relative paths. */
export const pickedFiles = (list: FileList): PickedFile[] => Array.from(list, (file) => ({ file, path: file.webkitRelativePath || file.name }));

/** Files from a drop, walking dropped folders. Read the entries before the first await: the browser
 *  empties the drop's item list once the event handler returns. */
export function droppedFiles(dt: DataTransfer): Promise<PickedFile[]> {
  const entries = Array.from(dt.items)
    .filter((i) => i.kind === 'file')
    .map((i) => i.webkitGetAsEntry?.())
    .filter((e): e is FileSystemEntry => !!e);
  if (!entries.length) return Promise.resolve(pickedFiles(dt.files));
  const out: PickedFile[] = [];
  const walk = async (entry: FileSystemEntry, prefix: string): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej));
      out.push({ file, path: prefix + entry.name });
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      // readEntries returns at most ~100 entries per call; keep reading until it returns none.
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
        if (!batch.length) break;
        for (const child of batch) await walk(child, `${prefix}${entry.name}/`);
      }
    }
  };
  return (async () => {
    for (const e of entries) await walk(e, '');
    return out;
  })();
}

export function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** PUT a file with progress (fetch cannot report upload progress). Resolves with the JSON body. */
export function uploadWithProgress(url: string, file: File, onProgress: (fraction: number) => void, meetingId?: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    const cred = meetingId ? guestCred.get(meetingId) : null;
    if (cred) xhr.setRequestHeader('Authorization', `Guest ${cred}`);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let data: { error?: string } = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* non-JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(data.error ?? `Upload failed (${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error('Upload failed: connection lost.'));
    xhr.send(file);
  });
}

/** Ask for a short-lived link, then open it (inline types) or download it. */
export async function openVersion(linkPath: string, download: boolean, meetingId?: string): Promise<void> {
  // Open the tab inside the tap, before any await: phones block window.open once the gesture has passed.
  const tab = download ? null : window.open('', '_blank');
  let url: string;
  try {
    ({ url } = await api<{ url: string }>(`${linkPath}${download ? `${linkPath.includes('?') ? '&' : '?'}download=1` : ''}`, { body: {}, meetingId }));
  } catch (err) {
    tab?.close();
    throw err;
  }
  if (tab) {
    tab.opener = null;
    tab.location.href = url;
    return;
  }
  if (download) {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(url, '_blank', 'noopener');
  }
}
